import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type {
  AdvertiserResponse,
  CredentialKeyStatusResponse,
  PartnerCredentialKind,
  PartnerCredentialRowResponse,
  ReencryptResponse,
} from '@leads-platform/shared';
import { Banner } from '../../components/ui/banner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Typeahead } from '../../components/typeahead';
import { StatusPill } from '../../components/ui/status-pill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/toast';
import { fmtDate } from '../../lib/format';
import {
  ApiError,
  createPartnerCredential,
  getPartnerCredentialKeyStatus,
  getPartnerCredentials,
  reencryptPartnerCredentials,
  revokePartnerCredential,
  rotatePartnerCredential,
} from './api';

/**
 * The credential kinds (shared `partnerCredentialKindEnum`) with their
 * user-facing label and the one-line entry helper for the add dialog.
 */
const KINDS: Record<PartnerCredentialKind, { label: string; helper: string }> = {
  bearer: { label: 'Bearer', helper: 'Sent as the Authorization: Bearer header.' },
  header: {
    label: 'Header',
    helper: 'Sent as the value of a custom header (the name is set on the campaign).',
  },
  query: { label: 'Query', helper: 'Appended to the advertiser URL as a query parameter.' },
  basic: { label: 'Basic', helper: 'Enter user:password' },
};

const KIND_ORDER: PartnerCredentialKind[] = ['bearer', 'header', 'query', 'basic'];

/** Narrow a `<select>` value back to the shared kind union — no casts. */
function toCredentialKind(value: string): PartnerCredentialKind {
  return value === 'header'
    ? 'header'
    : value === 'query'
      ? 'query'
      : value === 'basic'
        ? 'basic'
        : 'bearer';
}

/**
 * One error line from anything this screen can throw: an `ApiError` renders its
 * message plus any per-field validation errors; anything else degrades to the
 * message, then the caller's fallback.
 */
function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const fieldErrors = err.validationErrors
      ? Object.values(err.validationErrors).flat().join(' ')
      : '';
    return [err.message, fieldErrors].filter(Boolean).join(': ') || fallback;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

interface AddFormData {
  advertiserId: string;
  label: string;
  kind: PartnerCredentialKind;
  secret: string;
}

const initialAddForm: AddFormData = { advertiserId: '', label: '', kind: 'bearer', secret: '' };

/**
 * Partner credentials (release-plan 11a, ADR-010 §6, ADR-011) — the write-only
 * ops surface: list per advertiser, add, rotate, revoke, re-encrypt. The
 * write-only construction is the point (NFR-8): a stored secret is never shown
 * again, the API carries only a 4-character hint, and the key-status banner
 * tells the operator when the stack is running on the development key.
 */
export function PartnerCredentials() {
  const [rows, setRows] = useState<PartnerCredentialRowResponse[]>([]);
  const [keyStatus, setKeyStatus] = useState<CredentialKeyStatusResponse | null>(null);
  const [advertisers, setAdvertisers] = useState<AdvertiserResponse[]>([]);
  const [advertiserFilter, setAdvertiserFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<AddFormData>(initialAddForm);
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const [rotating, setRotating] = useState<PartnerCredentialRowResponse | null>(null);
  const [rotateSecret, setRotateSecret] = useState('');
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [rotatingBusy, setRotatingBusy] = useState(false);

  const [revoking, setRevoking] = useState<PartnerCredentialRowResponse | null>(null);
  const [revokingBusy, setRevokingBusy] = useState(false);

  const { success, error: toastError } = useToast();

  const loadRows = (advertiserId: string) => {
    getPartnerCredentials(advertiserId || undefined)
      .then(setRows)
      .catch((err: unknown) =>
        setError(describeError(err, 'Failed to load advertiser credentials'))
      );
  };

  const loadKeyStatus = () => {
    getPartnerCredentialKeyStatus()
      .then(setKeyStatus)
      .catch((err: unknown) => setError(describeError(err, 'Failed to load key status')));
  };

  useEffect(() => {
    api
      .getAdvertisers()
      .then(setAdvertisers)
      .catch(() => {
        // Best-effort: the filter and the add dialog just start empty.
      });
    loadRows('');
    loadKeyStatus();
  }, []);

  const refresh = () => {
    loadRows(advertiserFilter);
    loadKeyStatus();
  };

  const openAdd = () => {
    setAddForm({ ...initialAddForm, advertiserId: advertiserFilter });
    setAddError(null);
    setIsAddOpen(true);
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    setAddError(null);
    try {
      const row = await createPartnerCredential({
        advertiserId: addForm.advertiserId,
        label: addForm.label,
        kind: addForm.kind,
        secret: addForm.secret,
      });
      success(`Credential "${row.label}" stored`);
      setIsAddOpen(false);
      setAddForm(initialAddForm);
      refresh();
    } catch (err) {
      setAddError(describeError(err, 'Failed to store the credential'));
    } finally {
      setAdding(false);
    }
  };

  const openRotate = (row: PartnerCredentialRowResponse) => {
    setRotating(row);
    setRotateSecret('');
    setRotateError(null);
  };

  const closeRotate = () => {
    setRotating(null);
    setRotateSecret('');
    setRotateError(null);
  };

  const handleRotateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rotating === null) return;
    setRotatingBusy(true);
    setRotateError(null);
    try {
      const row = await rotatePartnerCredential(rotating.id, { secret: rotateSecret });
      success(`Credential "${row.label}" rotated`);
      closeRotate();
      refresh();
    } catch (err) {
      setRotateError(describeError(err, 'Failed to rotate the credential'));
    } finally {
      setRotatingBusy(false);
    }
  };

  const handleRevokeConfirm = async () => {
    if (revoking === null) return;
    setRevokingBusy(true);
    try {
      const row = await revokePartnerCredential(revoking.id);
      success(`Credential "${row.label}" revoked`);
      setRevoking(null);
      refresh();
    } catch (err) {
      toastError(describeError(err, 'Failed to revoke the credential'));
    } finally {
      setRevokingBusy(false);
    }
  };

  const handleReencrypt = async () => {
    try {
      const result: ReencryptResponse = await reencryptPartnerCredentials();
      const parts = [`${result.reencrypted} re-encrypted`, `${result.skipped} already current`];
      if (result.unknownKey > 0) parts.push(`${result.unknownKey} on an unknown key`);
      success(`Re-encrypt complete: ${parts.join(', ')}`);
      refresh();
    } catch (err) {
      toastError(describeError(err, 'Failed to re-encrypt credentials'));
    }
  };

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading advertiser credentials: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Advertiser Credentials</h1>
          <p className="text-muted-foreground mt-2">
            Stored encrypted. Only the last 4 characters are ever shown again.
          </p>
        </div>
        <Button onClick={openAdd}>Add credential</Button>
      </div>

      {keyStatus?.source === 'dev_default' && (
        <Banner tone="warn" className="mb-4">
          Development encryption key in use — do not store real advertiser credentials here
        </Banner>
      )}
      {keyStatus?.source === 'none' && (
        <Banner tone="stop" className="mb-4">
          No encryption key configured — credential writes are disabled
        </Banner>
      )}
      {keyStatus !== null && keyStatus.rowsOnPreviousKey > 0 && (
        <Banner tone="note" className="mb-4">
          {keyStatus.rowsOnPreviousKey} credentials still sit on the previous encryption key.{' '}
          <Button variant="outline" size="sm" className="ml-2" onClick={handleReencrypt}>
            Re-encrypt {keyStatus.rowsOnPreviousKey} credentials
          </Button>
        </Banner>
      )}

      <div className="flex items-center gap-2 mb-4">
        <Typeahead<AdvertiserResponse>
          aria-label="Filter by advertiser"
          className="w-64"
          options={advertisers}
          value={advertiserFilter || null}
          onChange={(v) => {
            const value = v ?? '';
            setAdvertiserFilter(value);
            loadRows(value);
          }}
          getId={(a) => a.id}
          getLabel={(a) => a.name}
          allOption={{ label: 'All advertisers' }}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Credentials</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Advertiser</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Hint</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Rotated</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No advertiser credentials stored. Add one to authenticate sends to an
                    advertiser.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="text-muted-foreground">{row.advertiserName}</TableCell>
                    <TableCell className="font-mono font-medium">{row.label}</TableCell>
                    <TableCell className="text-muted-foreground">{KINDS[row.kind].label}</TableCell>
                    <TableCell className="font-mono">
                      {row.hint ? `••••${row.hint}` : '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {fmtDate(row.createdAt)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {fmtDate(row.rotatedAt) || '—'}
                    </TableCell>
                    <TableCell>
                      <StatusPill value={row.revokedAt === null ? 'Active' : 'Revoked'} />
                    </TableCell>
                    <TableCell className="text-right">
                      {row.revokedAt === null && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openRotate(row)}>
                            Rotate
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setRevoking(row)}>
                            Revoke
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Credential</DialogTitle>
            <DialogDescription>
              The secret is encrypted at rest and never shown again — only its last 4 characters.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSubmit}>
            <div className="space-y-4 py-4">
              {addError && <p className="text-sm text-destructive">{addError}</p>}
              <div className="space-y-2">
                <Label htmlFor="advertiser">Advertiser</Label>
                <Typeahead<AdvertiserResponse>
                  id="advertiser"
                  options={advertisers}
                  value={addForm.advertiserId || null}
                  onChange={(v) => setAddForm({ ...addForm, advertiserId: v ?? '' })}
                  getId={(a) => a.id}
                  getLabel={(a) => a.name}
                  placeholder="Select advertiser…"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="label">Label</Label>
                <Input
                  id="label"
                  value={addForm.label}
                  onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
                  required
                  placeholder="e.g., Acme production token"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="kind">Kind</Label>
                <Select
                  id="kind"
                  value={addForm.kind}
                  onChange={(e) =>
                    setAddForm({ ...addForm, kind: toCredentialKind(e.target.value) })
                  }
                >
                  {KIND_ORDER.map((kind) => (
                    <option key={kind} value={kind}>
                      {KINDS[kind].label}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-muted-foreground">{KINDS[addForm.kind].helper}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="secret">Secret</Label>
                <Input
                  id="secret"
                  type="password"
                  autoComplete="off"
                  mono
                  value={addForm.secret}
                  onChange={(e) => setAddForm({ ...addForm, secret: e.target.value })}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={adding || !addForm.advertiserId}>
                {adding ? 'Saving...' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={rotating !== null}
        onOpenChange={(open) => {
          if (!open) closeRotate();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{rotating ? `Rotate "${rotating.label}"` : 'Rotate'}</DialogTitle>
            <DialogDescription>
              Stores a new secret on the same row. The previous secret stops working immediately.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRotateSubmit}>
            <div className="space-y-4 py-4">
              {rotateError && <p className="text-sm text-destructive">{rotateError}</p>}
              <div className="space-y-2">
                <Label htmlFor="rotate-secret">New secret</Label>
                <Input
                  id="rotate-secret"
                  type="password"
                  autoComplete="off"
                  mono
                  value={rotateSecret}
                  onChange={(e) => setRotateSecret(e.target.value)}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeRotate}>
                Cancel
              </Button>
              <Button type="submit" disabled={rotatingBusy}>
                {rotatingBusy ? 'Saving...' : 'Rotate'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={revoking !== null}
        onOpenChange={(open) => {
          if (!open) setRevoking(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{revoking ? `Revoke "${revoking.label}"` : 'Revoke'}</DialogTitle>
            <DialogDescription>
              Sends that reference this credential will be skipped with reason credential_revoked.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRevoking(null)}
              disabled={revokingBusy}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevokeConfirm} disabled={revokingBusy}>
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
